import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';
import { toast } from 'react-hot-toast';

export default function OrganizationWelcomeSettings({ orgId }) {
  const { currentUser } = useAuth();
  const [welcomeText, setWelcomeText] = useState('');
  const [welcomeVideoUrl, setWelcomeVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      try {
        const orgSnap = await getDoc(doc(db, 'organizations', orgId));
        if (orgSnap.exists()) {
          const data = orgSnap.data();
          const welcome = data.welcomeSettings || {};
          setWelcomeText(welcome.welcomeText || '');
          setWelcomeVideoUrl(welcome.welcomeVideoUrl || '');
        }
      } catch (e) {
        console.error('Error loading welcome settings:', e);
        toast.error('Failed to load welcome settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId]);

  const handleSave = async () => {
    if (!orgId || !currentUser?.uid) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'organizations', orgId), {
        welcomeSettings: {
          welcomeText: welcomeText.trim() || null,
          welcomeVideoUrl: welcomeVideoUrl.trim() || null,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
      });
      toast.success('Welcome settings saved');
    } catch (e) {
      console.error('Error saving welcome settings:', e);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome Message Settings</h2>
      <p className="text-gray-500 text-sm mb-6">Customize onboarding experience for new members.</p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Welcome text</label>
          <textarea
            value={welcomeText}
            onChange={(e) => setWelcomeText(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Message shown to new members when they join..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Welcome video URL</label>
          <input
            type="url"
            value={welcomeVideoUrl}
            onChange={(e) => setWelcomeVideoUrl(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://..."
          />
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-[#3274B8] text-white rounded-lg hover:bg-[#2861A1] disabled:opacity-50 font-medium"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
